import React from 'react';
import ReactImageFallback from 'react-image-fallback';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import { LomavisCompetitorIcon } from '@/lomavisSharedUi/atoms/icons/LomavisCompetitorIcon';
import { Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { LomavisTypeahead } from '../../inputs/LomavisTypeahead';
import { FACEBOOK, INSTAGRAM } from '@/config/constants';

interface CompetitorProfile {
  uuid: string;
  platform: 'instagram' | 'facebook' | 'twitter' | 'tiktok' | string; // extend as needed
  platform_id: string;
  username: string;
  name: string;
  profile_picture: string;
  followers_count: number;
  follows_count: number;
  media_count: number;
}

interface LomavisCompetitorSelectProps {
  /** The text to display inside the H1 heading */
  competitors: CompetitorProfile[];
  addCompetitor: (any) => void;
  removeCompetitor: (any) => void;
  currentPlatform?: string;
  fbSearchEndpoint?: string;
  addCompetitorEndpoint?: string;
  submittingCompetitor?: boolean;
  removingCompetitorUuid?: string | null;
  totalCompetitors?: number | null;
  maxCompetitors?: number | null;
}
interface ModalProps {
  show: boolean;
  onClose: () => void;
  competitors: any;
  addCompetitor: (any) => void;
  removeCompetitor: (any) => void;
  t: any;
  currentPlatform?: string;
  fbSearchEndpoint?: string;
  addCompetitorEndpoint?: string;
  submittingCompetitor?: boolean;
  removingCompetitorUuid?: string | null;
  totalCompetitors?: number | null;
  maxCompetitors?: number | null;
}

type ListCardProps = {
  image?: string;
  title: string;
  subtitle: string;
  highlight: boolean;
  fallback?: string;
  onClick: () => void;
  remove: () => void;
  isRemoving?: boolean; // new
};

const ListCard = ({
  image,
  title,
  subtitle,
  highlight,
  fallback,
  onClick,
  remove,
  isRemoving
}: ListCardProps) => {
  return (
    <div
      className={`w-100 d-flex p-3 justify-content-between align-items-center bg-light-primary rounded
      `}
      onClick={onClick}
    >
      <div className="d-flex align-items-center gap-2">
        {image && (
          <div className="me-3">
            <ReactImageFallback
              src={image}
              fallbackImage={fallback}
              initialImage={fallback}
              alt={title}
              className="w-50px h-50px rounded-circle"
            />
          </div>
        )}

        <div className="d-flex flex-column">
          <span className="text-gray-800 fw-bold text-hover-primary mb-1 fs-6">
            {title}
          </span>
          <span className="text-gray-500 fw-semibold d-block fs-7">
            {subtitle}
          </span>
        </div>
      </div>

      <span
        onClick={(e) => {
          e.stopPropagation();
          if (!isRemoving) remove();
        }}
        className="d-inline-flex align-items-center justify-content-center border border-2 border-secondary rounded-circle cursor-pointer"
        style={{ width: '40px', height: '40px' }}
      >
        {isRemoving ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <i className="fa-regular fa-xmark fs-3"></i>
        )}
      </span>
    </div>
  );
};
interface CompetitorSearchResult {
  id: string;
  display_name: string;
  image_url: string;
  platform_identifier: string;
}

const SelectModal: React.FC<ModalProps> = ({
  show,
  onClose,
  competitors,
  addCompetitor,
  removeCompetitor,
  t,
  currentPlatform,
  fbSearchEndpoint,
  addCompetitorEndpoint,
  submittingCompetitor,
  removingCompetitorUuid,
  totalCompetitors,
  maxCompetitors
}) => {
  const [manualInput, setManualInput] = React.useState('');
  const [addError, setAddError] = React.useState<string | null>(null);
  const [clearTrigger, setClearTrigger] = React.useState(0);

  const handleManualSubmit = async () => {
    if (!manualInput.trim()) return;
    try {
      setAddError(null); // clear previous error
      // eslint-disable-next-line
      addCompetitor({
        platform_id: manualInput,
        username: manualInput
      });
      setManualInput('');
    } catch (e) {
      setAddError(t('addCompetitor.errorIG')); // or any custom string
    }
    setManualInput(''); // clear after submit
  };

  const handleSelectCompetitor = async (competitor: CompetitorSearchResult) => {
    try {
      addCompetitor({
        platform_id: competitor?.id,
        username: competitor?.display_name
      });

      setClearTrigger((prev) => prev + 1);
    } catch (e) {
      // Handle error if needed
      console.error('Error adding competitor:', e);
    }
  };

  return (
    <Modal
      show={show}
      size="lg"
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop={'static'}
    >
      <Modal.Header className="d-flex flex-column justify-content-center">
        <span className="w-100 d-flex justify-content-center fs-2 mx-auto text-centered fw-bold">
          {t('addCompetitor')}
        </span>{' '}
        <span className="text-center fw-bold text-muted">
          {t(`addCompetitor.headerSub_${currentPlatform}`)}
        </span>
        {maxCompetitors && (
          <span className="w-100 d-flex justify-content-end text-muted">
            {totalCompetitors}/{maxCompetitors}
          </span>
        )}
      </Modal.Header>

      <Modal.Body
        style={{ height: '500px', overflowY: 'auto' }}
        className="p-3 px-4 d-flex flex-column gap-2"
      >
        {currentPlatform === FACEBOOK ? (
          <LomavisTypeahead<CompetitorSearchResult>
            endpoint={fbSearchEndpoint}
            labelKey="display_name"
            onSelect={(value) => {
              void handleSelectCompetitor(value);
            }}
            clearTrigger={clearTrigger}
            renderItem={(option) => (
              <div className="d-flex align-items-center gap-2">
                <img
                  src={(option as any).image_url}
                  alt={(option as any).display_name}
                  style={{ width: 30, height: 30, borderRadius: '50%' }}
                />
                <div>
                  <div>{(option as any).display_name}</div>
                  <small className="text-muted">
                    @{(option as any).platform_identifier}
                  </small>
                </div>
              </div>
            )}
            submitting={submittingCompetitor}
          />
        ) : (
          <InputGroup>
            <Form.Control
              type="text"
              value={manualInput}
              placeholder={t('addCompetitor.enterNameOrId')}
              onChange={(e) => {
                setAddError(null);
                setManualInput(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleManualSubmit();
              }}
            />
            <Button
              variant="primary"
              // eslint-disable-next-line no-void
              onClick={() => void handleManualSubmit()}
              disabled={submittingCompetitor}
            >
              {submittingCompetitor ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t('add')
              )}
            </Button>
          </InputGroup>
        )}

        {/* 🔴 Error text */}
        {currentPlatform === INSTAGRAM && addError && (
          <span className="text-danger mt-1 d-block fw-medium">{addError}</span>
        )}

        <div
          style={{ overflowY: 'auto', height: '100%' }}
          className="d-flex flex-column gap-2"
        >
          {competitors.map((item, index) => (
            <ListCard
              key={index}
              image={item?.profile_picture}
              title={item?.name}
              subtitle={item?.username}
              highlight={false}
              fallback={''}
              onClick={() => console.log('add logic here later')}
              remove={() => removeCompetitor(item?.uuid)}
              isRemoving={removingCompetitorUuid === item?.uuid} // pass loader flag
            />
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose}>{t('close')}</Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Molecules: LomavisCompetitorSelect
 */
export const LomavisCompetitorSelect: React.FC<
  LomavisCompetitorSelectProps
> = ({
  competitors,
  addCompetitor,
  removeCompetitor,
  currentPlatform,
  fbSearchEndpoint,
  addCompetitorEndpoint,
  submittingCompetitor,
  removingCompetitorUuid,
  totalCompetitors,
  maxCompetitors
}) => {
  const t = translateComponent('LomavisCompetitorSelect');

  //   const [selectedCompetitors, setselectedCompetitors] = useState();
  const [modalShow, setModalShow] = React.useState(false);

  return (
    <div className="border rounded">
      <div className="bg-white p-2 d-flex gap-1 align-items-center flex-wrap">
        {competitors?.map((item, index) => (
          <span key={item?.uuid} className="p-2">
            <LomavisCompetitorIcon
              image={item.profile_picture}
              platform={item.platform}
              onClick={() => removeCompetitor(item?.uuid)}
              isRemoving={removingCompetitorUuid === item?.uuid}
            />{' '}
          </span>
        ))}
        <span
          onClick={() => setModalShow(true)}
          className="d-flex justify-content-center align-items-center rounded-circle h-40px w-40px border m-2 cursor-pointer border-2"
        >
          <i className="fa-regular fa-plus fs-2"></i>
        </span>
      </div>

      <SelectModal
        show={modalShow}
        onClose={() => setModalShow(false)}
        competitors={competitors}
        addCompetitor={addCompetitor}
        removeCompetitor={removeCompetitor}
        t={t}
        currentPlatform={currentPlatform}
        fbSearchEndpoint={fbSearchEndpoint}
        addCompetitorEndpoint={addCompetitorEndpoint}
        submittingCompetitor={submittingCompetitor}
        removingCompetitorUuid={removingCompetitorUuid}
        totalCompetitors={totalCompetitors}
        maxCompetitors={maxCompetitors}
      />
    </div>
  );
};
